"use strict";
import _ from "lodash";
import { CLOUDINARY_LIB_URL, SID_LOOKUP } from "./app.constants";
import { homescreenState } from "./base.routes";
import {
    getContactWorkerDisplayName,
    getLatestMembership,
    lazyLoad,
} from "./common/common/common.utils";
import { ACTIVE } from "./common/communication/constants";

export const crewState = {
    name: "app.crew",
    url: "/crews/{crew_sid}",
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "crew" */ "./crew"),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        crew: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.retrieve($transition$.params().crew_sid);
        },
        farm: function (FarmService, crew) {
            "ngInject";
            return FarmService.retrieve(crew.farm);
        },
        grower: function (GrowerOrganizationService, farm) {
            "ngInject";
            return GrowerOrganizationService.retrieve(farm.grower_organization);
        },
    },
    redirectTo: homescreenState.name,
    data: {},
};

export const crewMessagesState = {
    name: "app.crew.messages",
    url: "/messages?state?owner",
    component: "crewConversationList",
    resolve: {
        conversations: function (ConversationService, $transition$, farm) {
            "ngInject";
            let params = {
                grower_organization__sid: farm.grower_organization,
                state: ACTIVE,
                contact__group__sid: $transition$.params().crew_sid,
            };
            let ownerParamValue = $transition$.params().owner;
            if (ownerParamValue === "unassigned") {
                params.owner__isnull = true;
            } else {
                params.owner__sid = ownerParamValue;
            }
            return ConversationService.list(params);
        },
        participants: function (AccountService, conversations) {
            "ngInject";
            let owners = _.map(conversations, "owner");
            let contacts = _.map(conversations, "contact");
            return AccountService.several(_.concat(owners, contacts));
        },
        beams: function (BeamsService, account) {
            "ngInject";
            BeamsService.initializeForConversations(account.sid);
        },
        twilioClient: function (ConversationService) {
            "ngInject";
            return ConversationService.createTwilioClient();
        },
        conversationData: function (conversations, participants) {
            "ngInject";
            return _.map(conversations, function (conversation) {
                return {
                    conversation: conversation,
                    contact: _.find(participants, [
                        SID_LOOKUP,
                        conversation.contact,
                    ]),
                    owner: _.find(participants, [
                        SID_LOOKUP,
                        conversation.owner,
                    ]),
                };
            });
        },
    },
    data: {
        title: "routes.CREW_MESSAGE_TITLE",
        mixpanelEvent: "Viewed crew messages",
        breadcrumb: "crumbs.CREW_MESSAGE",
    },
};

export const crewConversationState = {
    name: "app.crew.messages.conversation",
    url: "/{conversation_sid}",
    component: "crewConversation",
    resolve: {
        conversation: function (ConversationService, $transition$) {
            "ngInject";
            return ConversationService.retrieve(
                $transition$.params().conversation_sid,
            );
        },
        addedParticipant: function (conversation) {
            "ngInject";
            return conversation.addParticipant();
        },
        twilioClientConversation: function (
            ConversationService,
            addedParticipant,
        ) {
            "ngInject";
            return ConversationService.createTwilioClient();
        },
        twilioConversation: function (twilioClientConversation, conversation) {
            "ngInject";
            return twilioClientConversation.getConversationBySid(
                conversation.conversation_sid,
            );
        },
        participants: function (AccountService, conversation) {
            "ngInject";
            return AccountService.several([
                conversation.contact,
                conversation.owner,
            ]);
        },
        contact: function (participants, conversation) {
            "ngInject";
            return _.find(participants, [SID_LOOKUP, conversation.contact]);
        },
        owner: function (participants, conversation) {
            "ngInject";
            return _.find(participants, [SID_LOOKUP, conversation.owner]);
        },
        messages: function () {
            "ngInject";
            return [];
        },
        growerMemberships: function (
            GrowerMembershipService,
            $transition$,
            conversation,
            grower,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: grower.sid,
                user__sid: conversation.contact,
            });
        },
        contactWorker: function (contact, conversation, growerMemberships) {
            "ngInject";
            let growerMembership = getLatestMembership(
                growerMemberships,
                contact.sid,
            );
            return {
                user: contact,
                growerMembership,
                displayName: getContactWorkerDisplayName(
                    conversation,
                    growerMembership,
                ),
            };
        },
        newMessage: function () {
            "ngInject";
            return {};
        },
    },
    data: {
        title: "routes.CREW_CONVERSATION_TITLE",
        mixpanelEvent: "Viewed conversation",
        breadcrumb: "crumbs.CREW_CONVERSATION",
    },
};

export const crewConversationCreateState = {
    name: "app.crew.messages.create",
    url: "/create",
    component: "crewConversationCreate",
    resolve: {
        growerMemberships: function (
            GrowerMembershipService,
            grower,
            farm,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: grower.sid,
                user__group__sid: $transition$.params().crew_sid,
                active: true,
            });
        },
        employees: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.list({
                group__sid: $transition$.params().crew_sid,
            });
        },
        workers: function (employees, growerMemberships) {
            "ngInject";
            return _.map(employees, function (user) {
                return {
                    user: user,
                    growerMembership: getLatestMembership(
                        growerMemberships,
                        user.sid,
                    ),
                };
            });
        },
        conversation: function () {
            "ngInject";
            return {};
        },
    },
    data: {
        title: "routes.CREW_CONVERSATION_CREATE_TITLE",
        mixpanelEvent: "Viewed create conversation",
        breadcrumb: "crumbs.CREW_CONVERSATION_CREATE",
    },
};

export const crewAccidentReportState = {
    name: "app.crew.accidentReportCreate",
    url: "/report-accident",
    component: "crewAccidentReport",
    resolve: {
        growerMemberships: function (
            GrowerMembershipService,
            grower,
            farm,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: grower.sid,
                user__group__sid: $transition$.params().crew_sid,
                active: true,
            });
        },
        employees: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.list({
                group__sid: $transition$.params().crew_sid,
            });
        },
        workers: function (employees, growerMemberships) {
            "ngInject";
            return _.map(employees, function (user) {
                return {
                    user: user,
                    growerMembership: getLatestMembership(
                        growerMemberships,
                        user.sid,
                    ),
                };
            });
        },
    },
    data: {
        title: "routes.CREW_ACCIDENT_REPORT_TITLE",
        mixpanelEvent: "Viewed crew accident report",
        breadcrumb: "crumbs.CREW_ACCIDENT_REPORT",
    },
};
export const crewCreateAnnouncementBaseState = {
    name: "app.crew.send-announcement",
    url: "/{announcement_sid}",
    abstract: true,
    component: "crewAnnouncementBase",
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(
                /* webpackChunkName: "lazy.communication" */ "./lazy/communication"
            ),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
            );
        },
        crew: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.retrieve($transition$.params().crew_sid);
        },
        deleteRedirectState: function () {
            "ngInject";
            return homescreenState;
        },
        progressStates: function () {
            "ngInject";
            return [
                crewCreateAnnouncementAddRecipientsState,
                crewCreateAnnouncementAddContentsState,
                crewCreateAnnouncementAddAttachmentsState,
                crewCreateAnnouncementAddLocationState,
                crewCreateAnnouncementReviewAnnouncementState,
            ];
        },
    },
};
export const crewCreateAnnouncementAddRecipientsState = {
    name: "app.crew.send-announcement.addRecipients",
    url: "/select-recipients",
    component: "addRecipients",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
            );
        },
        recipientSids: function (announcement) {
            "ngInject";
            return announcement.recipients;
        },
        nextState: function () {
            "ngInject";
            return crewCreateAnnouncementAddContentsState;
        },
        reloadState: function () {
            "ngInject";
            return crewCreateAnnouncementBaseState;
        },
        redirectCrewsState: function () {
            "ngInject";
            return crewAddRecipientsCrewsState;
        },
    },
    data: {
        title: "routes.CREW_CREATE_ANNOUNCEMENT_ADD_RECIPIENTS_TITLE",
        mixpanelEvent: "Viewed new crew announcement add recipients",
    },
};
export const crewAddRecipientsCrewsState = {
    name: "app.crew.send-announcement.addRecipients.crews",
    url: "/crews",
    component: "addRecipientsCrews",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
            );
        },
        groups: function (crew) {
            "ngInject";
            return [crew];
        },
    },
    data: {
        title: "routes.CREW_ADD_RECIPIENTS_CREWS_TITLE",
        mixpanelEvent: "Viewed new announcement add recipients crews",
    },
};
export const crewCreateAnnouncementAddContentsState = {
    name: "app.crew.send-announcement.addContents",
    url: "/write-message",
    component: "addContents",
    resolve: {
        announcementTranslateds: function (
            AnnouncementTRANSLATEDService,
            $transition$,
        ) {
            "ngInject";
            return AnnouncementTRANSLATEDService.list({
                announcement__sid: $transition$.params().announcement_sid,
            });
        },
        previousState: function () {
            "ngInject";
            return crewCreateAnnouncementAddRecipientsState;
        },
        nextState: function () {
            "ngInject";
            return crewCreateAnnouncementAddAttachmentsState;
        },
        reloadState: function () {
            "ngInject";
            return crewCreateAnnouncementBaseState;
        },
    },
    data: {
        title: "routes.CREW_CREATE_ANNOUNCEMENT_ADD_CONTENTS_TITLE",
        mixpanelEvent: "Viewed new crew announcement write message",
    },
};
export const crewCreateAnnouncementAddAttachmentsState = {
    name: "app.crew.send-announcement.addAttachments",
    url: "/add-attachments",
    component: "addAttachments",
    lazyLoad: function ($transition$) {
        return lazyLoad($transition$, CLOUDINARY_LIB_URL);
    },
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
            );
        },
        currentAttachments: function (AttachmentService, $transition$) {
            "ngInject";
            return AttachmentService.list({
                announcement__sid: $transition$.params().announcement_sid,
            });
        },
        previousState: function () {
            "ngInject";
            return crewCreateAnnouncementAddContentsState;
        },
        nextState: function () {
            "ngInject";
            return crewCreateAnnouncementAddLocationState;
        },
        reloadState: function () {
            "ngInject";
            return crewCreateAnnouncementBaseState;
        },
    },
    data: {
        title: "routes.CREW_CREATE_ANNOUNCEMENT_ADD_ATTACHMENTS_TITLE",
        mixpanelEvent: "Viewed new crew announcement add attachments",
    },
};
export const crewCreateAnnouncementAddLocationState = {
    name: "app.crew.send-announcement.addLocation",
    url: "/add-location",
    component: "addLocation",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
            );
        },
        previousState: function () {
            "ngInject";
            return crewCreateAnnouncementAddAttachmentsState;
        },
        nextState: function () {
            "ngInject";
            return crewCreateAnnouncementReviewAnnouncementState;
        },
        reloadState: function () {
            "ngInject";
            return crewCreateAnnouncementBaseState;
        },
    },
    data: {
        title: "routes.CREW_CREATE_ANNOUNCEMENT_ADD_LOCATION_TITLE",
        mixpanelEvent: "Viewed new crew announcement add location",
    },
};
export const crewCreateAnnouncementReviewAnnouncementState = {
    name: "app.crew.send-announcement.reviewAnnouncement",
    url: "/review",
    component: "reviewAnnouncement",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
                {},
                {
                    expand: ["translations"],
                },
            );
        },
        sender: function (AccountService, announcement) {
            "ngInject";
            return AccountService.retrieve(announcement.sender);
        },
        recipients: function ($transition$, AccountService) {
            "ngInject";
            return AccountService.list({
                announcement__sid: $transition$.params().announcement_sid,
            });
        },
        attachments: function ($transition$, AttachmentService) {
            "ngInject";
            return AttachmentService.list({
                announcement__sid: $transition$.params().announcement_sid,
            }).then(function (results) {
                return _.groupBy(results, "language");
            });
        },
        previousState: function () {
            "ngInject";
            return crewCreateAnnouncementAddLocationState;
        },
        nextState: function () {
            "ngInject";
            return homescreenState;
        },
        scheduledNextState: function () {
            "ngInject";
            return homescreenState;
        },
    },
    data: {
        title: "routes.CREW_CREATE_ANNOUNCEMENT_REVIEW_ANNOUNCEMENT_TITLE",
        mixpanelEvent: "Viewed new crew announcement review",
    },
};
export const crewDisciplineReportBaseState = {
    name: "app.crewDisciplineReport",
    url: "/manager/{grower_sid}/record-employee-issue/{report_sid}",
    abstract: true,
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(
                /* webpackChunkName: "lazy.discipline" */ "./lazy/discipline"
            ),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        grower: function ($transition$, GrowerOrganizationService) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
    },
};
export const crewAddDetailsDisciplineReportState = {
    name: "app.crewDisciplineReport.addDetails",
    url: "/add-details",
    component: "addDetailsDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        users: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.list({
                growermembership__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
        workers: function (users, growerMemberships) {
            "ngInject";
            return _.map(growerMemberships, function (employee) {
                return {
                    user: _.find(users, [SID_LOOKUP, employee.user]),
                    growerMembership: employee,
                };
            });
        },
    },
    data: {
        title: "routes.CREW_ADD_DETAILS_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew add details discipline report",
    },
};
export const crewInstructionsDisciplineReportState = {
    name: "app.crewDisciplineReport.instructions",
    url: "/instructions",
    component: "instructionsDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
    },
    data: {
        title: "routes.CREW_INSTRUCTION_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew instructions discipline report",
    },
};
export const crewAddEmployeeStatementDisciplineReportState = {
    name: "app.crewDisciplineReport.addEmployeeStatement",
    url: "/add-employee-statement",
    component: "addEmployeeStatementDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
    },
    data: {
        title: "routes.CREW_ADD_EMPLOYEE_STATEMENT_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew add employee statement discipline report",
    },
};
export const crewAwaitApprovalDisciplineReportState = {
    name: "app.crewDisciplineReport.awaitApproval",
    url: "/wait-for-approval",
    component: "awaitApprovalDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
    },
    data: {
        title: "routes.CREW_AWAIT_APPROVAL_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew await approval discipline report",
    },
};
export const crewRecommendedActionDisciplineReportState = {
    name: "app.crewDisciplineReport.recommendedAction",
    url: "/recommended-action",
    component: "recommendedActionDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
        subjectEmployee: function (GrowerMembershipService, report) {
            "ngInject";
            return GrowerMembershipService.retrieve(report.subject_employee);
        },
        translated: function (
            $transition$,
            DisciplineReportTranslatedService,
            $translate,
        ) {
            "ngInject";
            return DisciplineReportTranslatedService.first({
                discipline_report__sid: $transition$.params().report_sid,
                language: $translate.use(),
            });
        },
        myGrowerMembership: function (
            account,
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                user__sid: account.sid,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.CREW_RECOMMENDED_ACTION_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew recommended action discipline report",
    },
};
export const crewAddOwnActionDisciplineReportState = {
    name: "app.crewDisciplineReport.recommendedAction.edit",
    url: "/edit",
    component: "addOwnActionDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
        myGrowerMembership: function (
            account,
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                user__sid: account.sid,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.CREW_ADD_OWN_ACTION_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew add own action discipline report",
    },
};
export const crewExecuteActionDisciplineReportState = {
    name: "app.crewDisciplineReport.executeAction",
    url: "/action",
    component: "executeActionDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
        subjectEmployee: function (GrowerMembershipService, report) {
            "ngInject";
            return GrowerMembershipService.retrieve(report.subject_employee);
        },
        translated: function (
            $transition$,
            DisciplineReportTranslatedService,
            $translate,
        ) {
            "ngInject";
            return DisciplineReportTranslatedService.first({
                discipline_report__sid: $transition$.params().report_sid,
                language: $translate.use(),
            });
        },
        myGrowerMembership: function (
            account,
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                user__sid: account.sid,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.CREW_EXECUTE_ACTION_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew execute action discipline report",
    },
};
export const crewDoneDisciplineReportState = {
    name: "app.crewDisciplineReport.done",
    url: "/done",
    component: "doneDisciplineReport",
    resolve: {
        report: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
    },
    data: {
        title: "routes.CREW_DONE_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew done discipline report",
    },
};
